import * as React from "react";
import { ArrowLeft, LayoutDashboard, ExternalLink, Maximize2, Minimize2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { trackActivity } from "@/utils/trackActivity";

const POWERBI_URL = "https://app.powerbi.com/view?r=eyJrIjoiMWU0YThiZWEtNjBiNS00YWI4LTk1ODUtMDY1OTI2NGUyMGUxIiwidCI6IjU4NjQ2NjQ1LWJiMDYtNDZkNS04OWEyLTdmNTU1OWRjNDMzMyIsImMiOjJ9";

export const MasterDashboardView = ({ onBack }) => {
  const [isFullscreen, setIsFullscreen] = React.useState(false);
  const iframeRef = React.useRef(null);

  React.useEffect(() => { trackActivity("view_module", "master_dashboard"); }, []);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      iframeRef.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  React.useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex flex-col">
      {/* Header */}
      <div className="border-b border-slate-700 bg-slate-900/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-full mx-auto px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={onBack}
                className="text-slate-300 hover:text-white hover:bg-slate-700"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Volver
              </Button>
              <div className="h-6 w-px bg-slate-600" />
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-gradient-to-br from-amber-500 to-orange-600">
                  <LayoutDashboard className="h-5 w-5 text-white" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h1 className="text-lg font-bold text-white">Master Dashboard</h1>
                    <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">
                      TIER 0
                    </Badge>
                  </div>
                  <p className="text-xs text-slate-400">Data homologada y estandarizada de todos los clientes</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleFullscreen}
                className="text-slate-300 hover:text-white hover:bg-slate-700"
              >
                {isFullscreen ? (
                  <><Minimize2 className="h-4 w-4 mr-2" />Salir</>
                ) : (
                  <><Maximize2 className="h-4 w-4 mr-2" />Pantalla Completa</>
                )}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.open(POWERBI_URL, '_blank')}
                className="border-slate-600 text-slate-300 hover:text-white hover:bg-slate-700"
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Abrir en Power BI
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Info Banner */}
      <div className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border-b border-amber-500/20 px-6 py-2">
        <div className="flex items-center justify-center gap-2 text-sm">
          <span className="text-amber-400 font-medium">ℹ️ TIER 0</span>
          <span className="text-slate-400">|</span>
          <span className="text-slate-300">
            Dashboard interno con métricas estandarizadas. Consulta cualquier cliente de la agencia con datos homologados.
          </span>
        </div>
      </div>

      {/* Power BI Iframe */}
      <div className="flex-1 p-4">
        <div 
          ref={iframeRef}
          className="w-full h-full rounded-lg overflow-hidden shadow-2xl border border-slate-700"
          style={{ minHeight: 'calc(100vh - 160px)' }}
        >
          <iframe
            title="Master Dashboard - TIER 0"
            src={POWERBI_URL}
            frameBorder="0"
            allowFullScreen
            className="w-full h-full"
            style={{ minHeight: 'calc(100vh - 160px)' }}
          />
        </div>
      </div>
    </div>
  );
};
